import dynamic from 'next/dynamic';
import NextImage from 'next/image';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import styled from 'styled-components';

import ScrollReveal from '@components/common/ScrollReveal';
import { Container } from '@components/parts/Container';

import { TDIs } from '@customTypes/TDI';

const Slider = dynamic(() => import('react-touch-drag-slider'), { ssr: false });

type Props = { tdis: TDIs };

const SliderWrapper = styled.div`
  height: 375px;
  @media (min-width: 992px) {
    height: 260px;
  }
  .rtds-slider-styles {
    font: inherit !important;
    word-break: inherit !important;
    .slide-outer {
      touch-action: pan-y !important;
    }
  }
  .rtds-single-slide-styles > div:first-child {
    padding: 0 !important;
  }
`;

const Wrapper = styled.div`
  user-select: none;
  height: 375px;
  @media (min-width: 992px) {
    height: 260px;
  }
  width: 100%;
  display: flex;
  background-color: #eff7ff;
  font-size: 20px;
  font-weight: 500;
  @media (min-width: 992px) {
    font-size: 26px;
  }
  div.quote {
    width: 100%;
    display: flex;
    flex-direction: column;
    .description {
      display: flex;
      flex: 1;
      flex-direction: column;
      max-height: 140px;
      overflow: hidden;
      text-overflow: ellipsis;
      margin: 65px 50px;
      @media (min-width: 992px) {
        margin: 60px 70px;
      }
    }
    .user {
      line-height: 1.7em;
      padding: 0 50px;
      display: flex;
      flex-direction: row;
      font-size: 0.7em;
      padding-bottom: 75px;
      div.name {
        flex: 1;
        margin-right: 40px;
        .hint {
          font-weight: 400;
          color: #2e3035;
        }
      }
    }
    @media (min-width: 992px) {
      flex-direction: row;
      .description {
        margin: 60px;
      }
      .user {
        padding: 0 60px;
        margin: 60px 0;
        border-left: 1px solid #c7cad0;
      }
    }
  }
`;

const SliderHintWrapper = styled.div`
  z-index: 900;
  position: absolute;
  bottom: 50px;
  left: 50px;
  @media (min-width: 992px) {
    left: 60px;
    bottom: 60px;
  }
  height: 4px;
  line-height: 0;
  div {
    display: inline-block;
    width: 20px;
    height: 4px;
    background-color: #e3e5ea;
    &.active {
      background-color: #055cf5;
    }
  }
`;

export const Quote: React.FC<Props> = (props) => {
  const { tdis } = props;
  const [currentSlide, _setCurrentSlide] = useState(0);
  const timerRef = useRef<any>(null);

  const setCurrentSlide = useCallback(
    (idx: number) => {
      if (idx >= tdis?.length) idx = 0;
      _setCurrentSlide(idx);
      timerRef.current = setTimeout(() => setCurrentSlide(idx + 1), 6000);
    },
    [tdis?.length]
  );

  const SliderHint: React.FC<{ activeIndex: number }> = ({ activeIndex }) => (
    <SliderHintWrapper>
      {tdis.map((_, index) => (
        <div key={`${index}`} className={`${index == activeIndex ? 'active' : ''}`} />
      ))}
    </SliderHintWrapper>
  );

  useEffect(() => {
    setCurrentSlide(0);
  }, [setCurrentSlide]);

  return (
    <ScrollReveal>
      <Container fullOnMobile>
        <SliderWrapper>
          <Slider
            onSlideComplete={(i) => {
              setCurrentSlide(i);
            }}
            onSlideStart={(i) => {
              if (timerRef.current) {
                clearInterval(timerRef.current);
              }
            }}
            activeIndex={currentSlide}
            threshHold={10}
            transition={0.5}
            scaleOnDrag={false}
          >
            {tdis.map((tdi, idx) => (
              <Wrapper key={`${idx}`}>
                <div className="quote">
                  <div className="description">
                    {tdi.description}
                    <br />
                  </div>
                  <div className="user">
                    <div className="name">
                      {tdi.title}
                      <br />
                      <span className="hint">{tdi.hint}</span>
                      <br />
                    </div>
                    <div>
                      {tdi.image?.data?.attributes?.url ? (
                        <NextImage src={tdi.image.data.attributes.url} alt={tdi.title} width={60} height={60} />
                      ) : null}
                    </div>
                  </div>
                </div>
              </Wrapper>
            ))}
          </Slider>
        </SliderWrapper>
        <div style={{ position: 'relative' }}>
          <SliderHint activeIndex={currentSlide} />
        </div>
      </Container>
    </ScrollReveal>
  );
};
